import { Badge } from '@/components/ui/badge'
import { Layers, LineChart, Share2, ShieldCheck, Workflow } from 'lucide-react'
import CrossroadsLogo from '../../../assets/images/full-logo-dark-mode.png'
import carriersImg from '../../../assets/images/landing-stock/carriers.png'
import dashboardImage from '../../../assets/images/landing-stock/dashboard.png'
import distributorsImg from '../../../assets/images/landing-stock/distributors.png'
import retailersImg from '../../../assets/images/landing-stock/retailers.png'
import suppliersImg from '../../../assets/images/landing-stock/suppliers.png'
import wholesalersImg from '../../../assets/images/landing-stock/wholesalers.png'

const customerTypes = [
  {
    title: 'Suppliers',
    description:
      'Legacy pricing services... The industry is still dependent on instant messaging, phone calls, and email to sell and purchase fuel.',
    image: suppliersImg,
    alt: 'Fuel supplier terminal',
  },
  {
    title: 'Wholesalers',
    description:
      'Keeping track of allocations and supply outages takes large amounts of manpower and causes disruptions. Delays in communication with carriers and customers can cause mis-pulls and delivery issues.',
    image: wholesalersImg,
    alt: 'Wholesale distribution center',
  },
  {
    title: 'Retailers',
    description:
      'Retailers lack visibility into the order lifecycle causing uncertainty and phone calls. Supply volatility and outages cause extra strain for a common carrier delivery.',
    image: retailersImg,
    alt: 'Retail gas station',
  },
  {
    title: 'Carriers',
    description:
      'It is challenging to hire and train new dispatchers because every customer has different requirements and different systems. Supply outages, price changes, and terminal wait times waste time and money. Delays in paperwork & credit/re-bills extend payment timelines and strain carrier cash flow.',
    image: carriersImg,
    alt: 'Fuel carrier truck',
  },
  {
    title: 'Customers',
    description:
      'Uncertainty regarding cost and delivery can strain the relationships with wholesalers and carriers. Manual processes and delays in paperwork make reconciliation a large burden.',
    image: distributorsImg,
    alt: 'Commercial fleet customer',
  },
]

const solutionHighlights = [
  {
    label: 'Universal data model for every terminal, site, tank, and product',
    icon: Layers,
  },
  {
    label: 'Pre-mapped network of counterparties and systems ready to connect',
    icon: Share2,
  },
  {
    label: 'Agents automate ingestion, normalization, and delivery of data',
    icon: Workflow,
  },
  {
    label: 'Quality and governance keep information trusted across teams',
    icon: ShieldCheck,
  },
  {
    label: 'Dashboards and proactive alerts surface the right action in time',
    icon: LineChart,
  },
]

export const AboutSection = () => {
  return (
    <section className='relative z-20 py-32 bg-white'>
      <div className='container mx-auto px-8 flex flex-col gap-28'>
        <div className='flex flex-col gap-7'>
          <h1 className='text-4xl font-medium lg:text-7xl text-gray-900'>
            The fuel industry runs on relationships.
          </h1>
        </div>

        {/* The Problem Section */}
        <div className='flex flex-col gap-12'>
          <div>
            <h3 className='text-3xl font-medium md:text-5xl text-gray-900 mb-6'>
              THE PROBLEM
            </h3>
            <p className='max-w-xl text-lg text-gray-600'>
              The fuel industry involves thousands of counterparties using
              hundreds of systems that don't talk. The same data gets keyed over
              and over or companies build costly, fragile, one-off integrations.
              The industry is losing millions to integration friction that
              shouldn't exist in the AI age.
            </p>
          </div>

          {/* Customer Types with alternating layouts */}
          {customerTypes.map((customer, index) => (
            <div key={customer.title} className='grid gap-6 md:grid-cols-2'>
              {index % 2 === 0 ? (
                <>
                  <img
                    src={customer.image}
                    alt={customer.alt}
                    className='w-full max-h-96 rounded-2xl object-cover'
                  />
                  <div className='flex flex-col justify-center gap-6 rounded-2xl bg-gray-50 p-10'>
                    <h2 className='text-2xl font-medium text-gray-900'>
                      {customer.title}
                    </h2>
                    <p className='text-lg text-gray-700'>
                      {customer.description}
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <div className='flex flex-col justify-center gap-6 rounded-2xl bg-gray-50 p-10'>
                    <h2 className='text-2xl font-medium text-gray-900'>
                      {customer.title}
                    </h2>
                    <p className='text-lg text-gray-700'>
                      {customer.description}
                    </p>
                  </div>
                  <img
                    src={customer.image}
                    alt={customer.alt}
                    className='w-full max-h-96 rounded-2xl object-cover'
                  />
                </>
              )}
            </div>
          ))}
        </div>
        <div className='flex flex-col gap-10 md:gap-16'>
          <div className='flex items-center justify-between'>
            <h2 className='text-3xl font-medium md:text-5xl text-gray-900'>
              THE SOLUTION
            </h2>
            <img
              src={CrossroadsLogo}
              alt='Crossroads Logo'
              className='h-12 w-auto'
              style={{ filter: 'invert(1)' }}
            />
          </div>
          <div className='relative overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-xl'>
            <div className='pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(45,40,75,0.08),transparent_60%)]' />
            <div className='relative grid gap-12 p-8 md:p-12 lg:grid-cols-3'>
              <div className='flex flex-col justify-between gap-10'>
                <div>
                  <Badge
                    variant='outline'
                    className='border-gray-300 text-gray-700'
                  >
                    Crossroads Platform
                  </Badge>
                  <h3 className='mt-8 text-3xl font-medium text-gray-900 md:text-4xl'>
                    Structure, network, and automation—delivered together.
                  </h3>
                  <p className='mt-5 text-lg text-gray-600'>
                    Crossroads captures the fuel supply chain in a digital twin,
                    maps every counterparty, and keeps information flowing with
                    agents so teams can focus on growth instead of re-keying
                    data.
                  </p>
                </div>
                <div className='flex flex-col gap-3'>
                  {solutionHighlights.map((item, index) => (
                    <div key={item.label} className='flex flex-col'>
                      <div className='flex items-center gap-3 text-gray-600'>
                        <item.icon className='h-4 w-4 text-[#2D284B]' />
                        <span>{item.label}</span>
                      </div>
                      {index < solutionHighlights.length - 1 && (
                        <div
                          className='my-3 h-px w-full bg-gray-200'
                          aria-hidden='true'
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
              <div className='lg:col-span-2'>
                <div className='relative flex aspect-[4/3] w-full items-end overflow-hidden rounded-2xl shadow-2xl'>
                  <img
                    src={dashboardImage}
                    alt='Crossroads dashboard'
                    className='h-full w-full object-cover'
                  />
                  <div className='absolute inset-0 bg-gradient-to-t from-[#2D284B]/85 via-[#2D284B]/20 to-transparent' />
                  <div className='relative flex w-full flex-col gap-4 px-8 pb-8 text-white md:px-10 md:pb-10'>
                    <img
                      src={CrossroadsLogo}
                      alt='Crossroads platform'
                      className='h-10 w-auto'
                      style={{ filter: 'invert(1)' }}
                    />
                    <p className='max-w-lg text-sm text-white/80 md:text-base'>
                      Plug Crossroads into your ecosystem and let agents
                      orchestrate clean, trusted data across every integration
                      and stakeholder.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
